import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Loader2, Plus, Trash2, CheckCircle2, Clock, Gavel, XCircle, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { nok } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUS = {
  foreslått: { label: "Foreslått av styret", cls: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400", Icon: Clock },
  vedtatt: { label: "Vedtatt av GF", cls: "border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400", Icon: Gavel },
  utbetalt: { label: "Utbetalt", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", Icon: CheckCircle2 },
  forkastet: { label: "Forkastet", cls: "border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400", Icon: XCircle },
};

export function DividendsTab({ companyId, canManage, resultat }) {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    () => api.get(`/companies/${companyId}/dividends`).then((r) => setList(r.data.dividends || [])).catch(() => setList([])),
    [companyId]
  );
  useEffect(() => { load(); }, [load]);

  const propose = async () => {
    const num = Number(amount);
    if (Number.isNaN(num) || num <= 0) {
      toast.error("Skriv inn et utbyttebeløp større enn 0");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/dividends`, { year: Number(year), total_amount: num });
      toast.success("Utbytte foreslått – generalforsamling er opprettet automatisk");
      setAmount("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const pay = async (id) => {
    try {
      await api.post(`/companies/${companyId}/dividends/${id}/pay`);
      toast.success("Utbytte markert som utbetalt");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/companies/${companyId}/dividends/${id}`);
      toast.success("Utbytte slettet");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="dividends-tab">
      {canManage ? (
        <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
          <h3 className="flex items-center gap-1.5 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <Plus className="h-4 w-4 text-primary" /> Foreslå utbytte
          </h3>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Regnskapsår</Label>
              <Input data-testid="dividend-year-input" type="number" className="mt-1.5 h-9 w-28" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Totalt utbytte (kr)</Label>
              <Input data-testid="dividend-amount-input" type="number" inputMode="numeric" className="mt-1.5 h-9 w-48" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
            </div>
            <Button className="h-9 gap-2" data-testid="dividend-propose-btn" onClick={propose} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />} Foreslå
            </Button>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
            Når styret foreslår, opprettes automatisk en <span className="font-medium text-foreground">generalforsamling</span> med saken «Vedtak om utbytte». Utbyttet blir <span className="font-medium text-foreground">Vedtatt</span> når generalforsamlingen vedtar saken, og kan deretter markeres som utbetalt.
            {typeof resultat === "number" ? (
              <> Til referanse: årsresultat er <span className="font-semibold text-foreground">{nok(resultat)}</span>.</>
            ) : null}
          </p>
        </div>
      ) : null}

      {list === null ? (
        <p className="p-8 text-center text-sm text-muted-foreground">Laster …</p>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground" data-testid="dividends-empty">
          <Coins className="h-8 w-8 text-muted-foreground/50" />
          Ingen utbytter er foreslått ennå.
        </div>
      ) : (
        list.map((d) => {
          const st = STATUS[d.status] || STATUS.foreslått;
          return (
            <div key={d.id} className="rounded-xl border border-border/60 bg-card shadow-sm" data-testid={`dividend-${d.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 p-4">
                <div>
                  <p className="font-headings text-lg font-bold tracking-tight">{nok(d.total_amount)}</p>
                  <p className="text-xs text-muted-foreground">Regnskapsår {d.year} · foreslått av {d.proposed_by_name}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${st.cls}`} data-testid={`dividend-status-${d.id}`}>
                    <st.Icon className="h-3.5 w-3.5" /> {st.label}
                  </span>
                  {d.meeting_id ? (
                    <Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid={`dividend-gf-${d.id}`} onClick={() => navigate(`/selskap/${companyId}/moter/${d.meeting_id}`)}>
                      <Gavel className="h-4 w-4" /> Generalforsamling <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                  {(d.status === "vedtatt" || d.status === "utbetalt") ? (
                    <Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid={`dividend-pdf-${d.id}`} onClick={() => openSigned(`/companies/${companyId}/dividends/${d.id}/pdf-link`).catch(() => toast.error("Kunne ikke åpne protokollen"))}>
                      <FileText className="h-4 w-4" /> Protokoll (PDF)
                    </Button>
                  ) : null}
                  {canManage && d.status === "vedtatt" ? (
                    <Button size="sm" className="h-8 gap-1.5" data-testid={`dividend-pay-${d.id}`} onClick={() => pay(d.id)}>
                      <CheckCircle2 className="h-4 w-4" /> Marker utbetalt
                    </Button>
                  ) : null}
                  {canManage ? (
                    <button data-testid={`dividend-delete-${d.id}`} onClick={() => remove(d.id)} className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:text-destructive" title="Slett">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wider">Aksjonær</TableHead>
                    <TableHead className="text-right text-xs uppercase tracking-wider">Eierandel</TableHead>
                    <TableHead className="text-right text-xs uppercase tracking-wider">Utbytte</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(d.allocations || []).map((a) => (
                    <TableRow key={a.shareholder_id}>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{a.ownership_pct}%</TableCell>
                      <TableCell className="text-right font-semibold">{nok(a.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          );
        })
      )}
    </div>
  );
}
